import React from 'react'
import styled, { css, createGlobalStyle } from 'styled-components'
import AntSelect from 'antd/lib/select'
import { text, getLineHeightPx } from '../styles/mixins/text'
import { color } from '../styles'
import { insertTestingSelector, TestingKey } from '../util/testing'
import { LabelPosition, InputContainer } from './InputContainer'
import { SPACING } from '../styles/mixins/constants'
import { AppTheme } from '../styles/theme'
import { isKeyPressEnter } from '../util/isKeyPressEnter'

export type SelectOption = {
    value: string
    label: string
}

export type SelectProps = {
    value?: string | null
    placeholder?: string
    isLoading?: boolean
    disabled?: boolean
    accented?: boolean
    menuWidthPx?: number
    squareRightBorder?: boolean
    label?: string
    labelPosition?: LabelPosition
    error?: string
    hasDanger?: boolean
    options: Array<SelectOption>
    testingKeys?: [TestingKey] | [TestingKey, string]
    noOptionsMessage?: string
    className?: string
    noPadding?: boolean
    disableClear?: boolean
    disableSearch?: boolean
    disableBorder?: boolean
    hideIndicator?: boolean
    backgroundColor?: keyof AppTheme['colors']['background']
    onChange?: (value: string | null, label: string | null | undefined) => void
    onSearch?: (value?: string | null | undefined) => Promise<void>
    onEnterPress?: () => void
}

export const Select = (props: SelectProps): JSX.Element => {
    const {
        value,
        options,
        placeholder,
        disabled,
        isLoading,
        error,
        hasDanger,
        label,
        testingKeys,
        noOptionsMessage,
        className,
        disableSearch,
        disableBorder,
        labelPosition,
        disableClear,
        menuWidthPx,
        backgroundColor,
        onChange,
        onSearch,
        onEnterPress,
    } = props

    let testingParams = {}
    if (testingKeys) {
        const [key, modifier] = Array.isArray(testingKeys) ? testingKeys : [testingKeys]
        testingParams = insertTestingSelector(key, modifier)
    }
    const shouldDisplayError = Boolean(error || hasDanger)
    const optionTestingClass = testingKeys?.[0] ? `${testingKeys?.[0]}-Option` : undefined

    let optionsWithTestingClass = options

    if (optionTestingClass) {
        optionsWithTestingClass = (options || []).map((option) => ({
            ...option,
            className: `${optionTestingClass}`,
        }))
    }
    return (
        <Container
            className={className}
            hasDanger={Boolean(shouldDisplayError)}
            labelPosition={labelPosition}
            label={label}
            disabled={Boolean(disabled)}
            errorMessage={error}
            disableBorder={disableBorder}
            backgroundColor={backgroundColor}
        >
            <Global menuWidthPx={menuWidthPx} />
            <AntSelect
                {...testingParams}
                onSelect={(_, option) => {
                    let nextLabel = ''
                    if (typeof option.label === 'string') nextLabel = option.label
                    onChange?.(option.value, nextLabel)
                }}
                onSearch={onSearch}
                onInputKeyDown={(e) => {
                    if (!isKeyPressEnter(e)) return
                    onEnterPress?.()
                }}
                onClear={() => {
                    onChange?.(null, null)
                    onSearch?.(null)
                }}
                showSearch={!disableSearch}
                optionFilterProp="label"
                loading={isLoading}
                disabled={disabled}
                allowClear={!disableClear}
                placeholder={placeholder}
                options={optionsWithTestingClass}
                value={value || undefined}
                notFoundContent={
                    noOptionsMessage ? <NoneMesage>{noOptionsMessage}</NoneMesage> : undefined
                }
            />
        </Container>
    )
}

const NoneMesage = styled.div`
    ${text('paragraph', 'secondary', 'text')}
`

const SelectDropdownStyles = css`
    .ant-select,
    .ant-select-selector {
        flex: 1 1 auto;
        min-height: 0;
        width: 100%;
    }

    .ant-select-dropdown * {
        ${text('paragraph')}
    }

    ${(p: { menuWidthPx?: number }) =>
        p.menuWidthPx
            ? css`
                  .ant-select-dropdown {
                      min-width: ${p.menuWidthPx}px!important;
                  }
              `
            : ''}
`

const Global = createGlobalStyle`
  ${SelectDropdownStyles}
`

const ERROR = css`
    && .ant-select-selector {
        border: 1px solid ${color.status.danger};
    }

    .ant-select-arrow {
        color: ${color.status.danger};
    }
`

type InputProps = {
    hasDanger: boolean
    menuWidth?: number
    disableBorder?: boolean
    backgroundColor?: keyof AppTheme['colors']['background']
}

const ContainerStyles = css<InputProps>`
    && .ant-select-selector {
        padding: ${SPACING[1]} ${SPACING[3]};
        background-color: ${color.background.control};
        border: 1px solid ${color.border.primary};
        height: 37px;
        ${(p) =>
            p.disableBorder
                ? css`
                      border-width: 0px;
                  `
                : ''}
        ${(p) =>
            p.backgroundColor
                ? css`
                      background-color: ${color.background[p.backgroundColor]({
                          theme: p.theme,
                      })};
                  `
                : ''}
    }

    .ant-select,
    .ant-select-selection-item {
        ${text('paragraph')}
        line-height: ${getLineHeightPx('paragraph')}px;
    }

    ${(p) => p.hasDanger && ERROR}

    ${(p) =>
        p.menuWidth
            ? css`
                  .ant-select-selector {
                      min-width: ${p.menuWidth};
                  }
              `
            : ''}
`
const Container = styled(InputContainer)`
    ${ContainerStyles}
`

Select.Styles = {
    SelectDropdownStyles,
    ContainerStyles,
}
